import { servingAmountsToInputs } from "shared-ui/ServingAmount/utils";
import { serializeDateTime } from "@notemeal/utils-date-time";
import { OnSaveMenuItemArgs } from "apps/web/src/views/MenuItems/Dialog/Edit/MenuItemEditDialog";
import { getMenuItemChoiceDiffInputs, getMenuItemChoiceInput } from "../../MenuItemAddOns/utils";
import { FetchResult, MutationFunctionOptions } from "@apollo/client";
import {
  CreateReusableMenuItemInput,
  CreateReusableMenuItemMutation,
  EditReusableMenuItemInput,
  EditReusableMenuItemMutation,
  EditReusableMenuItemPartialInput,
  EditReusableMenuItemPartialMutation,
  Exact,
} from "apps/web/src/types";
import { allergenFormStateFromRecipe, allergensChanged, selfAllergensInput } from "../../Recipe/Form/allergens";
import { MenuItemState } from "../reducer";
import { SaveMenuItemAllergens } from "./useSaveMenuItemAllergens";

interface OnEditProps {
  state: OnSaveMenuItemArgs;
  editReusableMenuItem: (
    options?:
      | MutationFunctionOptions<
          EditReusableMenuItemMutation,
          Exact<{
            input: EditReusableMenuItemInput;
          }>
        >
      | undefined
  ) => Promise<FetchResult<EditReusableMenuItemMutation>>;
  editReusableMenuItemPartial: (
    options?:
      | MutationFunctionOptions<
          EditReusableMenuItemPartialMutation,
          Exact<{
            input: EditReusableMenuItemPartialInput;
          }>
        >
      | undefined
  ) => Promise<FetchResult<EditReusableMenuItemPartialMutation>>;
  saveAllergens: SaveMenuItemAllergens;
}

export const onEdit = async ({ editReusableMenuItem, editReusableMenuItemPartial, saveAllergens, state }: OnEditProps) => {
  const { initial, final } = state;
  let savedMenuItemId: string | undefined;
  if (final.isRevision) {
    const { data } = await editReusableMenuItem({
      variables: {
        input: {
          menuItemId: final.menuItem.id,
          after: serializeDateTime(new Date()),
          name: final.menuItem.name,
          servingName: final.menuItem.servingName,
          description: final.menuItem.description,
          defaultMaxAmount: final.maxAmount,
          defaultAvailableForOrder: final.availableForOrder,
          defaultAllowSpecialRequests: final.allowSpecialRequests,
          servingAmounts: servingAmountsToInputs(final.menuItem.servingAmounts),
          ...getMenuItemChoiceDiffInputs({
            initial: initial.choices,
            final: final.menuItem.choices,
          }),
          imageUrl: final.menuItem.imageUrl,
          suggestionCategory: final.menuItem.suggestionCategory,
          scoreValue: final.menuItem.score?.value ?? null,
          foodCategory: final.menuItem.foodCategory?.category ?? null,
        },
      },
    });
    savedMenuItemId = data?.editReusableMenuItem.menuItem.id;
  } else {
    const { data } = await editReusableMenuItemPartial({
      variables: {
        input: {
          id: final.menuItem.id,
          name: final.menuItem.name,
          servingName: final.menuItem.servingName,
          description: final.menuItem.description,
          defaultMaxAmount: final.maxAmount,
          defaultAvailableForOrder: final.availableForOrder,
          defaultAllowSpecialRequests: final.allowSpecialRequests,
          imageUrl: final.menuItem.imageUrl,
          suggestionCategory: final.menuItem.suggestionCategory,
          scoreValue: final.menuItem.score?.value ?? null,
          foodCategory: final.menuItem.foodCategory?.category ?? null,
        },
      },
    });
    savedMenuItemId = data?.editReusableMenuItemPartial.menuItem.id;
  }
  // After a revision the allergens belong on the new menu item, which carried the old ones forward
  if (savedMenuItemId && allergensChanged(allergenFormStateFromRecipe(initial), final)) {
    await saveAllergens(savedMenuItemId, selfAllergensInput(final));
  }
};

interface OnCreateProps {
  createReusableMenuItem: (
    options?:
      | MutationFunctionOptions<
          CreateReusableMenuItemMutation,
          Exact<{
            input: CreateReusableMenuItemInput;
          }>
        >
      | undefined
  ) => Promise<FetchResult<CreateReusableMenuItemMutation>>;
  saveAllergens: SaveMenuItemAllergens;
  state: MenuItemState;
}

export const onCreate = async ({ createReusableMenuItem, saveAllergens, state }: OnCreateProps) => {
  const { menuItem, maxAmount, availableForOrder, allowSpecialRequests } = state;
  const { data } = await createReusableMenuItem({
    variables: {
      input: {
        name: menuItem.name,
        servingName: menuItem.servingName,
        description: menuItem.description,
        defaultMaxAmount: maxAmount,
        defaultAvailableForOrder: availableForOrder,
        defaultAllowSpecialRequests: allowSpecialRequests,
        servingAmounts: servingAmountsToInputs(menuItem.servingAmounts),
        choices: menuItem.choices.map(getMenuItemChoiceInput),
        imageUrl: menuItem.imageUrl,
        suggestionCategory: menuItem.suggestionCategory,
        scoreValue: menuItem.score?.value ?? null,
        foodCategory: menuItem.foodCategory?.category ?? null,
      },
    },
  });
  const selfAllergens = selfAllergensInput(state);
  // null means unreviewed, which is what a new menu item already is
  if (data && selfAllergens !== null) {
    await saveAllergens(data.createReusableMenuItem.menuItem.id, selfAllergens);
  }
};
